// src/cockpit4/workspaces/CEOWorkspace.tsx
// ============================================
// CEO Workspace - Cockpit 4.0 首页
//
// 设计：私募融资感 + Bloomberg Terminal 风格
//   - 顶部：Hermes 晨报区域（全宽）- Good Morning + 今日 3 件事
//   - 中部：4 个 KPI 卡片（今日收入/客户/内容/任务）
//   - 底部：Live Company 实时流（滚动动画，新条目从顶部滑入）
//
// 数据来源（优先级从高到低）：
//   1. EnterpriseBrainRuntime.get_today_focus — 企业总脑（真正的战略分析）
//   2. BusinessOperator.run_daily_check — 运营检查器（降级 1）
//   3. activities 派生 — 活动流（降级 2）
//
// KPI：useDashboardData 的 CEOData
// Live Company：useCockpitData 的 activities
// ============================================

import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun,
  Star,
  TrendingUp,
  Users,
  FileText,
  CheckSquare,
  Activity,
  Sparkles,
  ArrowUpRight,
  Brain,
  Loader2,
  X,
  AlertTriangle,
  Flame,
  Target,
  Handshake,
  MessageSquare,
  ChevronDown,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useCockpitData } from "../../hooks/useCockpitData";
import { formatYuanWithSymbol } from "../../components/Dashboard/shared";
import { useCockpit4Store, type WorkspaceId, type CrmRiskItem } from "../store";
import ErrorBoundary from "../components/ErrorBoundary";
import PlansPanel from "../components/PlansPanel";
import AnnualGoalsEntry from "../components/AnnualGoalsEntry";
import AssetBuildingDashboard from "../components/AssetBuildingDashboard";
import KnowledgeFlywheelEntry from "../components/KnowledgeFlywheelEntry";
import FiveLayerPlanDrilldown from "../components/FiveLayerPlanDrilldown";
// P0-功能1：CEO 智能晨报（数据摘要+异常预警+AI建议+对标分析+写入Vault）
import CEOMorningReport from "../components/CEOMorningReport";
// 第十一个 Runtime：昨天公司学到了什么卡片（第十五章 Knowledge Evolution Protocol）
import YesterdayLearningsCard from "../components/YesterdayLearningsCard";
// P0-功能2：CEO 决策面板（AI建议卡片+规则引擎+一键执行+历史记录）
import CEODecisionPanel from "../components/CEODecisionPanel";
// V1.5 P0-1：今日赚钱机会条带（Autopilot 机会排序器 → 首页 Top3）
import TodayOpportunityStrip from "../components/TodayOpportunityStrip";
// V1.5 P1-7：CEO Command Center 首屏指挥条（值多少钱 / Hermes 在做啥 / 健康度）
import CommandCenterStrip from "../components/CommandCenterStrip";
// P0-功能3：五维风险预警雷达（财务/客户/运营/市场/人才五维健康度+下钻）
import RiskRadarChart from "../components/RiskRadarChart";
// P0-功能4：增强版 KPI 卡片（sparkline+目标进度+降级标记+下钻）
import KpiCard from "../components/KpiCard";
// P1-功能5：OKR 目标管理体系（树形结构+五层下钻+复盘报告）
import OKRManager from "../components/OKRManager";
// P1-功能6：战略地图（平衡计分卡四维度+AI 评估报告）
import StrategyMap from "../components/StrategyMap";
// P1-功能7：经营会议模拟（CMO/CSO/CFO/COO 四部门汇报+流式输出+Vault 纪要）
import BusinessMeetingSimulator from "../components/BusinessMeetingSimulator";
// P1-功能8：企业健康度评分（五维度+趋势图+改进建议）
import HealthScoreDashboard from "../components/HealthScoreDashboard";
// P2-功能9：AI 团队看板（9 个 AI 岗位状态 + 负载率 + 产出）
import AITeamDashboard from "../components/AITeamDashboard";
// P2-功能11：预测性经营模拟（30/90 天预测 + 多情景 + 营销杠杆）
import PredictiveSimulator from "../components/PredictiveSimulator";
// P2-功能10：人机协作管理（AI 做/人做的事 + 协作效率分析）
import HumanMachineManagement from "../components/HumanMachineManagement";
// P3-v6.45：经营驾驶舱（获客→成交结果漏斗 + 运营状态 + 风险卡片 + 承诺账本，实现盈利功能）
import BusinessResultDashboard from "../components/BusinessResultDashboard";
// P3-v6.45：多角色议题辩论室（战略/风控/执行 + 主席裁决，对标李开复多智能体决策）
import DebateRoom from "../components/DebateRoom";

// ============================================
// 类型定义
// ============================================

// V1.5 P1-7：下部 Tab 折叠为「今日必看 / 更多」两级
/** 「今日必看」核心页签（老板每天最该看的结果页，平铺显示） */
const MUST_SEE_TABS = [
  { id: "morning", label: "智能晨报", icon: Sun, group: "P0" },
  { id: "decision", label: "决策建议", icon: Sparkles, group: "P0" },
  { id: "p3_result", label: "经营驾驶舱", icon: Handshake, group: "P3" },
  { id: "p1_health", label: "健康度", icon: Activity, group: "P1" },
] as const;

/** 「更多」页签（低频功能收进二级菜单） */
const MORE_TABS = [
  { id: "strategy", label: "战略计划", icon: Target, group: "P0" },
  { id: "assets", label: "资产增长", icon: TrendingUp, group: "P0" },
  { id: "risk", label: "风险监控", icon: AlertTriangle, group: "P0" },
  { id: "p3_debate", label: "议题辩论", icon: MessageSquare, group: "P3" },
  { id: "p1_okr", label: "OKR 管理", icon: Target, group: "P1" },
  { id: "p1_meeting", label: "经营会议", icon: Users, group: "P1" },
  { id: "p2_ai_team", label: "AI团队", icon: Users, group: "P2" },
] as const;

/** 晨报优先级条目 */
interface MorningPriority {
  title: string;
  /** 高优先级 / 中优先级 / 低优先级 */
  level: "high" | "medium" | "low";
  /** 来源模块（如 EnterpriseBrain / CRM / Marketing / Projects） */
  source?: string;
  /** 理由（为什么这件事重要） */
  rationale?: string;
  /** 预期成果 */
  expectedOutcome?: string;
  /** 涉及产品 */
  product?: string;
}

/** 晨报数据 */
interface MorningBriefing {
  /** 优先级列表（今日 3 件事） */
  priorities: MorningPriority[];
  /** 数据来源：enterprise_brain / business_operator / activities */
  source: "enterprise_brain" | "business_operator" | "activities";
  /** 整体优先级星级（1-5） */
  overallPriority?: number;
  /** 风险列表 */
  risks?: string[];
  /** 机会列表 */
  opportunities?: string[];
}

// ============================================
// CEO Workspace 主组件
// ============================================

/** V1.5 P0-3：自主等级（Level 0-4，默认 L1 AI 辅助）
 *  Level 0 人工：人决定，AI 只执行指令
 *  Level 1 AI 辅助：AI 建议，人点执行
 *  Level 2 目标驱动：给定目标，AI 自动拆解路径并执行
 *  Level 3 半自主：常规动作自动执行，异常/异常建议需批准
 *  Level 4 受控自主：长期稳定任务自动执行，但发布/付款/战略修改/重大数据变更仍人工确认（白皮书 Rule 03）
 */
const AUTONOMY_LEVELS: Array<{ id: string; label: string; desc: string }> = [
  { id: "l0", label: "L0 人工", desc: "人决定，AI 只执行指令" },
  { id: "l1", label: "L1 AI辅助", desc: "AI 建议，人点执行" },
  { id: "l2", label: "L2 目标驱动", desc: "给定目标，AI 自动拆解路径执行" },
  { id: "l3", label: "L3 半自主", desc: "常规自动，异常需批准" },
  { id: "l4", label: "L4 受控自主", desc: "发布/付款/战略修改仍人工确认" },
];

export default function CEOWorkspace() {
  const dashboard = useDashboardData();
  const cockpitData = useCockpitData();
  const ceoData = dashboard.ceo;
  const activities = cockpitData.activities;

  const [briefing, setBriefing] = useState<MorningBriefing>({
    priorities: [],
    source: "activities",
  });

  // 真实 KPI 数据状态（替代硬编码）
  const [realKpi, setRealKpi] = useState<{
    contentCount: number;
    taskDone: number;
    taskTotal: number;
  } | null>(null);
  // v6.3.24 今日内容/今日任务各自的查询失败标记（失败才降级；查到 0 是真实状态）
  const [realKpiError, setRealKpiError] = useState<{
    content: boolean;
    tasks: boolean;
  }>({ content: false, tasks: false });

  // 拉取真实 KPI 数据（不消耗 token，仅查询本地统计）
  useEffect(() => {
    let cancelled = false;
    const loadRealKpi = async () => {
      try {
        // 等待 RuntimeManager 就绪
        for (let i = 0; i < 20; i++) {
          if (cancelled) return;
          if (RuntimeManager.isInitialized()) break;
          await new Promise((r) => setTimeout(r, 300));
        }
        if (cancelled) return;

        // 查询今日内容数（v6.3.25 与内容工厂拉通：改用 ContentRuntime today_content_stats，
        // 统计 XEAOS/ContentPackages/{今日}/*.md —— 内容工厂/媒体工作室生成的脚本/文章即自动累计）
        let contentCount = 0;
        let contentFailed = false;
        try {
          if (RuntimeManager.hasRuntime(RuntimeName.Content)) {
            const resp = await RuntimeManager.execute(RuntimeName.Content, "today_content_stats", {});
            const inner = resp?.data as any;
            if (inner && typeof inner === "object") {
              contentCount = Number(inner.total ?? 0);
            } else {
              contentFailed = true;
            }
          }
        } catch {
          contentFailed = true;
        }

        // 查询今日任务（从 Obsidian Daily 计划解析）
        // v6.3.25 兼容两种文件名：标准 Daily/{yyyy-mm-dd}.md + AI COO 生成的 经营计划-{yyyy-mm-dd}.md；
        // 文件不存在（404）是"今日暂无计划"的真实状态，不算降级；只有网络/接口异常才算降级
        let taskDone = 0;
        let taskTotal = 0;
        let tasksFailed = false;
        try {
          const today = new Date().toISOString().slice(0, 10);
          // 1) 先读标准 Daily/{yyyy-mm-dd}.md
          let content: string | null = null;
          try {
            const resp = await fetch(
              `/vault-api/read?path=${encodeURIComponent(
                `10_Operations（运营层）/Daily/${today}.md`
              )}`
            );
            if (resp.ok) {
              const json = await resp.json();
              if (json?.success && typeof json.content === "string") {
                content = json.content;
              }
            } else if (resp.status !== 404) {
              tasksFailed = true; // 非"文件不存在"的接口异常才算降级
            }
          } catch {
            tasksFailed = true;
          }
          // 2) 404 时兜底：列出 Daily 目录，找文件名含今日日期的 .md（如 经营计划-2026-08-10.md）
          if (!content && !tasksFailed) {
            try {
              const listResp = await fetch(
                `/vault-api/list?dir=${encodeURIComponent(
                  `10_Operations（运营层）/Daily`
                )}&ext=.md`
              );
              if (listResp.ok) {
                const listJson = await listResp.json();
                const files: any[] = listJson?.files || [];
                const todayFile = files.find((f) =>
                  String(f.name || "").includes(today)
                );
                if (todayFile) {
                  const readResp = await fetch(
                    `/vault-api/read?path=${encodeURIComponent(
                      `10_Operations（运营层）/Daily/${todayFile.name}`
                    )}`
                  );
                  if (readResp.ok) {
                    const readJson = await readResp.json();
                    if (readJson?.success && typeof readJson.content === "string") {
                      content = readJson.content;
                    }
                  }
                }
              }
            } catch {
              // 兜底扫描失败不视为降级（主路径已 404，属真实无计划）
            }
          }
          // 3) 解析任务清单：- [x] 已完成，- [ ] 未完成
          if (content) {
            const doneMatches = content.match(/- \[x\]/gi);
            const todoMatches = content.match(/- \[ \]/gi);
            taskDone = doneMatches?.length || 0;
            taskTotal = taskDone + (todoMatches?.length || 0);
          }
        } catch {
          tasksFailed = true;
        }

        if (!cancelled) {
          setRealKpi({ contentCount, taskDone, taskTotal });
          setRealKpiError({ content: contentFailed, tasks: tasksFailed });
        }
      } catch {
        // 整体降级
      }
    };
    loadRealKpi();
    return () => {
      cancelled = true;
    };
  }, []);

  // 拉取晨报：三级降级策略
  // 1. EnterpriseBrain.get_today_focus（企业总脑）
  // 2. BusinessOperator.run_daily_check（运营检查）
  // 3. activities 派生（活动流）
  // Token 优化：晨报加载改为手动触发，不在挂载时自动调用 BusinessOperator（避免 LLM 调用）
  const [briefingLoading, setBriefingLoading] = useState(false);
  // 晨报生成结果提示（给用户可见反馈，解决"点击无反应"）
  const [briefingMessage, setBriefingMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // ============================================
  // 查看 Daily 文件（闭环验证：让用户直接看到 10_Operations/Daily/ 下已生成的文件）
  // 存储位置：本地 Obsidian D:/Obsidian/小舍AI_中央大脑库/10_Operations（运营层）/Daily/
  //           服务器 obsidian-vault（通过 /vault-api 读写，与本地保持同步）
  // ============================================
  const [dailyFiles, setDailyFiles] = useState<{ name: string; path: string; mtime: string }[]>([]);
  const [dailyFilesLoading, setDailyFilesLoading] = useState(false);
  const [showDailyFiles, setShowDailyFiles] = useState(false);

  // Phase4-UX：首次使用业务闭环引导（可关闭，localStorage 记忆状态）
  const [showOnboard, setShowOnboard] = useState<boolean>(
    () => !localStorage.getItem("xeaos_onboarding_dismissed")
  );
  const dismissOnboard = useCallback(() => {
    setShowOnboard(false);
    try {
      localStorage.setItem("xeaos_onboarding_dismissed", "1");
    } catch {
      // ignore
    }
  }, []);

  // 业务闭环 6 节点 → 快捷跳转
  const CLOSED_LOOP_STEPS: { id: string; label: string; desc: string; ws: WorkspaceId; color: string }[] =
    [
      { id: "mine", label: "① 线索挖掘", desc: "GEO+DSO 泛词获客", ws: "geodso", color: "#22D3EE" },
      { id: "content", label: "② 内容生产", desc: "日更品牌脚本", ws: "media_studio", color: "#A855F7" },
      { id: "publish", label: "③ 发布引流", desc: "多平台分发", ws: "media_studio", color: "#EC4899" },
      { id: "pool", label: "④ 线索入池", desc: "留资建档", ws: "crm", color: "#3B82F6" },
      { id: "deal", label: "⑤ 洽谈成交", desc: "跟进回款", ws: "crm", color: "#10B981" },
      { id: "finance", label: "⑥ 记账复盘", desc: "收入核销", ws: "finance", color: "#F59E0B" },
    ];

  const loadDailyFiles = useCallback(async () => {
    setDailyFilesLoading(true);
    try {
      const resp = await fetch(
        `/vault-api/list?dir=${encodeURIComponent("10_Operations（运营层）/Daily")}&ext=.md`
      );
      if (!resp.ok) {
        setDailyFiles([]);
        return;
      }
      const json = await resp.json();
      const files: { name: string; path: string; mtime: string }[] = Array.isArray(json?.files)
        ? json.files
            .filter((f: any) => !f.isDirectory)
            .sort((a: any, b: any) => String(b.mtime || "").localeCompare(String(a.mtime || "")))
            .map((f: any) => ({ name: f.name, path: f.path, mtime: f.mtime || "" }))
        : [];
      setDailyFiles(files);
    } catch {
      setDailyFiles([]);
    } finally {
      setDailyFilesLoading(false);
    }
  }, []);


  const loadBriefing = useCallback(async () => {
    setBriefingLoading(true);
    setBriefingMessage(null);
    let cancelled = false;
    try {
      const date = new Date().toISOString().slice(0, 10);

      // 生成晨报并持久化到 Vault（两个数据源共用，保证闭环）
      // 写入路径：10_Operations（运营层）/Daily/晨报-{date}.md
      const buildAndPersist = async (
        priorities: MorningPriority[],
        source: MorningBriefing["source"],
        risks: string[] = [],
        opportunities: string[] = []
      ) => {
        setBriefing({ priorities, source, overallPriority: 5, risks, opportunities });
        try {
          const briefingContent = [
            `---`,
            `type: morning_briefing`,
            `date: ${date}`,
            `created_at: ${new Date().toISOString()}`,
            `status: generated`,
            `source: ${source}`,
            `---`,
            ``,
            `# 🌅 今日晨报 · ${date}`,
            ``,
            `## 今日优先级（3 件事）`,
            ``,
            ...priorities.map(
              (p, i) =>
                `${i + 1}. **${p.title}**（${p.level === "high" ? "高" : p.level === "medium" ? "中" : "低"}优先级）${p.rationale ? ` — ${p.rationale}` : ""}`
            ),
            ``,
            ...(risks.length > 0
              ? [`## 风险提示`, ``].concat(risks.map((r) => `- ${r}`), [``])
              : []),
            ...(opportunities.length > 0
              ? [`## 机会`, ``].concat(opportunities.map((o) => `- ${o}`), [``])
              : []),
          ].join("\n");
          await fetch(
            `/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Daily/晨报-${date}.md`)}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ content: briefingContent }),
            }
          );
          return `10_Operations（运营层）/Daily/晨报-${date}.md`;
        } catch {
          return "写入 Vault 失败（本地可到 Obsidian 检查）";
        }
      };

      // Level 1: EnterpriseBrain（企业总脑）- 优先读缓存，不消耗 token
      try {
        if (RuntimeManager.hasRuntime(RuntimeName.EnterpriseBrain)) {
          const resp = await RuntimeManager.execute(
            RuntimeName.EnterpriseBrain,
            "get_today_focus",
            { date }
          );
          if (cancelled) return;
          if (resp?.success && resp.data) {
            const data = resp.data as any;
            const priorities: MorningPriority[] = extractBrainPriorities(data);
            if (priorities.length > 0) {
              const filePath = await buildAndPersist(
                priorities,
                "enterprise_brain",
                data?.risks || [],
                data?.opportunities || []
              );
              if (cancelled) return;
              setBriefingMessage({
                type: "ok",
                text: `✅ 今日晨报已生成（企业总脑）\n📄 已写入 Vault：${filePath}\n点击下方「查看 Daily 文件」可确认落盘`,
              });
              return;
            }
          }
          // EnterpriseBrain 返回空 → 明确提示，不再静默
          setBriefingMessage({
            type: "err",
            text: "⚠️ 企业总脑暂无数据，可先到「战略计划」生成今日计划后再生成晨报",
          });
        }
      } catch (err) {
        console.warn("[CEOWorkspace] EnterpriseBrain 调用失败", err);
      }

      // Level 2: activities 派生（不消耗 token，但同样落盘闭环）
      if (cancelled) return;
      let activitiesPriorities = derivePrioritiesFromActivities(activities);

      // 增强：优先从今日计划（10_Operations/Daily/{date}.md）读取真实任务作为晨报优先级
      try {
        const dailyResp = await fetch(
          `/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Daily/${date}.md`)}`
        );
        if (dailyResp.ok) {
          const dailyJson = await dailyResp.json();
          if (dailyJson?.success && dailyJson.content) {
            const dailyPriorities = derivePrioritiesFromDailyPlan(dailyJson.content);
            if (dailyPriorities.length > 0) activitiesPriorities = dailyPriorities;
          }
        }
      } catch { /* 忽略，使用 activities 兜底 */ }

      const filePath = await buildAndPersist(activitiesPriorities, "activities");
      if (cancelled) return;
      setBriefingMessage({
        type: "ok",
        text: `✅ 晨报已从今日活动流生成（企业总脑暂不可用）\n📄 已写入 Vault：${filePath}\n点击下方「查看 Daily 文件」可确认落盘`,
      });
    } finally {
      if (!cancelled) setBriefingLoading(false);
    }
  }, [activities]);

  // 挂载时只尝试读取 EnterpriseBrain 缓存（不消耗 token），不自动触发 BusinessOperator
  useEffect(() => {
    let cancelled = false;
    const tryCacheOnly = async () => {
      const date = new Date().toISOString().slice(0, 10);
      try {
        if (RuntimeManager.hasRuntime(RuntimeName.EnterpriseBrain)) {
          const resp = await RuntimeManager.execute(
            RuntimeName.EnterpriseBrain,
            "get_today_focus",
            { date }
          );
          if (cancelled) return;
          if (resp?.success && resp.data) {
            const data = resp.data as any;
            const priorities: MorningPriority[] = extractBrainPriorities(data);
            if (priorities.length > 0) {
              setBriefing({
                priorities,
                source: "enterprise_brain",
                overallPriority: data?.overallPriority || 5,
                risks: data?.risks || [],
                opportunities: data?.opportunities || [],
              });
              return;
            }
          }
        }
      } catch {
        // 静默失败，不消耗 token
      }
      // 降级到 activities 派生（不调 LLM）
      if (cancelled) return;
      setBriefing({
        priorities: derivePrioritiesFromActivities(activities),
        source: "activities",
      });
    };
    tryCacheOnly();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切换工作区（用于 KPI 卡片点击跳转）
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  // CRM 联动的风险客户列表（来自 CRMWorkspace 提取）
  const crmRisks = useCockpit4Store((s) => s.crmRisks);

  // 当前选中的优先级条目（用于展开详情 Modal）
  const [activePriority, setActivePriority] = useState<MorningPriority | null>(null);

  // 下部面板 Tab：智能晨报 / 决策建议 / 战略计划 / 资产增长 / 风险监控 / 知识飞轮 / P1-OKR管理 / P1-战略地图 / P1-经营会议 / P1-健康度（避免页面过长、信息杂乱）
  const [activeSection, setActiveSection] = useState<
    | "morning" | "decision" | "strategy" | "assets" | "risk" | "flywheel"
    | "p1_okr" | "p1_strategy_map" | "p1_meeting" | "p1_health"
    | "p2_ai_team" | "p2_predictive" | "p2_human_ai"
    | "p3_result" | "p3_debate"
  >("morning");
  // V1.5 P1-7：下部 Tab 折叠为「今日必看 / 更多」两级
  const [showMoreTabs, setShowMoreTabs] = useState(false);

  // 4 个 KPI 卡片数据（使用真实数据替代硬编码）
  // P0-功能4：增强 KPI 卡片（sparkline+同比环比+目标完成率+下钻）
  // v6.3.24 逐卡独立降级判定：查询失败才显示"降级"角标；查询成功返回 0 是真实状态，正常显示数字

  // V1.5 P0-3：年度目标 → OKR → 日目标自动拆解（替代写死占位值）
  // 读取与 AnnualGoalsEntry 相同的 Vault 年度目标文件，按月目标 / 21 工作日拆日目标
  const [goalTargets, setGoalTargets] = useState<{
    dailyRevenue: number | null;
    dailyCustomers: number | null;
  }>({ dailyRevenue: null, dailyCustomers: null });

  useEffect(() => {
    let cancelled = false;
    const loadGoalTargets = async () => {
      try {
        const paths = [
          "11_Strategy（战略层）/年度目标-2026.md",
          "11_Strategy（战略层）/Annual-Goals-2026.md",
          "10_Operations（运营层）/年度目标-2026.md",
        ];
        let content = "";
        for (const p of paths) {
          try {
            const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
            if (resp.ok) {
              const result = await resp.json();
              if (result.success && result.content) {
                content = result.content;
                break;
              }
            }
          } catch {
            // 尝试下一个路径
          }
        }
        if (cancelled || !content) return;

        // 解析月收入目标 / 月新增客户目标 → 日目标（21 个工作日）
        const WORK_DAYS = 21;
        let revNum: number | null = null;
        for (const re of [/月收入目标[：:\s]*[¥￥]?\s*([\d,]+)/, /月营收目标[：:\s]*[¥￥]?\s*([\d,]+)/]) {
          const m = content.match(re);
          if (m) {
            revNum = Number(m[1].replace(/,/g, ""));
            break;
          }
        }
        let custNum: number | null = null;
        for (const re of [/月新增客户目标[：:\s]*([\d,]+)/, /月新客目标[：:\s]*([\d,]+)/]) {
          const m = content.match(re);
          if (m) {
            custNum = Number(m[1].replace(/,/g, ""));
            break;
          }
        }
        setGoalTargets({
          dailyRevenue: revNum && revNum > 0 ? Math.round(revNum / WORK_DAYS) : null,
          dailyCustomers: custNum && custNum > 0 ? Math.max(1, Math.round(custNum / WORK_DAYS)) : null,
        });
      } catch {
        // 静默：未配置目标时不造假，保持 null（KPI 卡片显示"未设目标"）
      }
    };
    void loadGoalTargets();
    return () => {
      cancelled = true;
    };
  }, []);

  // V1.5 P0-3：自主等级（Level 0-4，默认 L1 AI 辅助）— 发布/付款/战略修改始终需人工确认
  const [autonomyLevel, setAutonomyLevel] = useState<string>(
    () => localStorage.getItem("xeaos_autonomy_level") || "l1"
  );
  const cycleAutonomy = () => {
    const idx = AUTONOMY_LEVELS.findIndex((l) => l.id === autonomyLevel);
    const next = AUTONOMY_LEVELS[(idx + 1) % AUTONOMY_LEVELS.length];
    setAutonomyLevel(next.id);
    localStorage.setItem("xeaos_autonomy_level", next.id);
  };

  const kpiCards = useMemo(() => {
    const revenue = ceoData.kpi.revenue;
    const newCustomers = ceoData.kpi.newCustomers;
    // 真实数据：从 KnowledgeRuntime 资产统计 + Obsidian Daily 计划获取
    const contentCount = realKpi?.contentCount ?? 0;
    const taskDone = realKpi?.taskDone ?? 0;
    const taskTotal = realKpi?.taskTotal ?? 0;
    const taskRate = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;
    // 收入趋势数据（用于 sparkline，仅当有真实数据时展示）
    const revenueTrend: number[] =
      ceoData.revenueTrend && ceoData.revenueTrend.length >= 2
        ? ceoData.revenueTrend.map((p) => p.revenue)
        : [];
    // v6.3.24 逐源降级标记（undefined → 回退全局判定）
    const src = ceoData.sourceStatus;
    const financeDegraded = src?.finance ?? ceoData.degraded;
    const crmDegraded = src?.crm ?? ceoData.degraded;
    // 真实状态文案（非降级时展示，说明"0 是真实状态"而非故障）
    const noDataHint = "暂无数据（真实状态）：在财务中心录入 / CRM 建档 / 内容工厂生产 / 战略计划生成今日计划后自动显示";
    return [
      {
        label: "今日收入",
        value: formatYuanWithSymbol(revenue).replace("¥", "¥ "),
        icon: TrendingUp,
        color: "#10B981",
        subtitle: "收入",
        deltaPercent: undefined, // 暂无对比数据，留空不展示 delta
        deltaLabel: "环比",
        target: goalTargets.dailyRevenue ?? undefined, // V1.5 P0-3：OKR 月目标自动拆解日目标（未配置时为 undefined → 隐藏进度条）
        actual: revenue,
        progressUnit: "%",
        trend: revenueTrend,
        degraded: financeDegraded,
        dataSource: financeDegraded
          ? "财务查询失败（Enterprise Runtime 未返回）"
          : revenue > 0
          ? "实时查询 Obsidian Vault 财务记录"
          : noDataHint,
        onClick: () => setWorkspace("finance"),
      },
      {
        label: "今日客户",
        value: `+${newCustomers}`,
        icon: Users,
        color: "#3B82F6",
        subtitle: "新客户",
        deltaPercent: undefined,
        deltaLabel: "环比",
        target: goalTargets.dailyCustomers ?? undefined, // V1.5 P0-3：OKR 月目标自动拆解日目标（未配置时为 undefined → 隐藏进度条）
        actual: newCustomers,
        progressUnit: "%",
        trend: undefined, // 暂无客户趋势数据
        degraded: crmDegraded,
        dataSource: crmDegraded
          ? "CRM 查询失败（CRM Runtime 未返回）"
          : newCustomers > 0
          ? "实时查询 CRM 客户中心"
          : noDataHint,
        onClick: () => setWorkspace("crm"),
      },
      {
        label: "今日内容",
        value: `${contentCount} 篇`,
        icon: FileText,
        color: "#EC4899",
        subtitle: "内容产出",
        deltaPercent: undefined,
        deltaLabel: "环比",
        target: 1, // 每日内容目标 1 篇
        actual: contentCount,
        progressUnit: "%",
        trend: undefined,
        degraded: realKpiError.content,
        dataSource: realKpiError.content
          ? "内容统计失败（Content Runtime 未返回）"
          : contentCount > 0
          ? "实时统计内容工厂产物（ContentPackages/今日/*.md，品牌脚本自动累计）"
          : "今日暂无内容产出（真实状态）— 到内容工厂生成品牌脚本后自动统计",
        onClick: () => setWorkspace("media_studio"),
      },
      {
        label: "今日任务",
        value: `${taskDone}/${taskTotal}`,
        icon: CheckSquare,
        color: "#A855F7",
        subtitle: `完成率 ${taskRate}%`,
        deltaPercent: undefined,
        deltaLabel: "环比",
        target: 100, // 任务完成率目标 100%
        actual: taskRate,
        progressUnit: "%",
        trend: undefined,
        degraded: realKpiError.tasks,
        dataSource: realKpiError.tasks
          ? "任务统计失败（Daily 计划读取异常）"
          : taskTotal > 0
          ? "实时解析 Obsidian Daily 今日计划"
          : "今日暂无计划（真实状态）— 到战略计划生成今日计划后自动统计",
        onClick: () => setWorkspace("ceo"),
      },
    ];
  }, [ceoData, realKpi, realKpiError, setWorkspace]);

  // 当前时间问候语
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 6) return "夜深了，Andy";
    if (hour < 12) return "Good Morning, Andy";
    if (hour < 18) return "Good Afternoon, Andy";
    return "Good Evening, Andy";
  }, []);

  const todayDate = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  return (
    <div className="h-full w-full flex flex-col gap-3 overflow-y-auto p-4">
      {/* ============ Phase4-UX：首次使用业务闭环引导（可关闭） ============ */}
      {showOnboard && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, height: 0 }}
          className="relative flex-shrink-0 rounded-xl border border-purple-500/20 bg-gradient-to-r from-purple-500/[0.06] to-blue-500/[0.04] px-4 py-3 overflow-hidden"
        >
          <div className="flex items-start gap-3">
            <div className="flex flex-col gap-1.5 flex-1">
              <div className="flex items-center gap-2">
                <Sparkles size={13} className="text-purple-400" />
                <span className="text-[12px] font-medium text-white tracking-tight">
                  第一次使用？跟着这条业务闭环跑一遍
                </span>
              </div>
              <p className="text-[10px] text-gray-400 leading-relaxed max-w-3xl">
                XEAOS 是一个「获客→成交」的自动经营闭环。从上面任一环节点进去，系统会自动联动下一环，数据实时回流到这里：
              </p>
              {/* 链路节点 */}
              <div className="flex items-center flex-wrap gap-y-2 mt-1">
                {CLOSED_LOOP_STEPS.map((s, i) => (
                  <div key={s.id} className="flex items-center">
                    {i > 0 && <span className="mx-0.5 text-gray-600 text-[10px]">→</span>}
                    <button
                      onClick={() => setWorkspace(s.ws)}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.08] hover:border-white/15 transition-colors group"
                      title={`前往${s.desc}`}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: s.color }}
                      />
                      <span className="text-[10px] font-medium text-gray-200 group-hover:text-white">
                        {s.label}
                      </span>
                      <span className="text-[9px] text-gray-500 group-hover:text-gray-400">
                        {s.desc}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={dismissOnboard}
              title="关闭引导"
              className="p-1 rounded text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              <X size={13} />
            </button>
          </div>
        </motion.div>
      )}
      {/* ============ 顶部：Hermes 晨报区域 ============ */}
      <motion.section
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/80 to-[#0a0e1a]/80 backdrop-blur-xl overflow-hidden flex-shrink-0"
      >
        {/* 装饰：右上角紫色光晕 */}
        <div
          className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(168,85,247,0.18) 0%, transparent 70%)",
            filter: "blur(30px)",
          }}
        />
        {/* 装饰：左下角蓝色光晕 */}
        <div
          className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)",
            filter: "blur(30px)",
          }}
        />
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/60 to-transparent" />

        <div className="relative p-6">
          {/* 头部：问候 + 日期 */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  animate={{ opacity: [0, 0.4, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                <Sun size={18} className="relative text-white" />
              </div>
              <div>
                <h1 className="text-[22px] font-semibold text-white tracking-tight">
                  {greeting}
                </h1>
                <p className="text-[11px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                  Today is {todayDate} ·{" "}
                  {briefing.source === "enterprise_brain"
                    ? "Enterprise Brain 已生成"
                    : briefing.source === "business_operator"
                    ? "BusinessOperator 已生成"
                    : "每日 08:30 定时自动生成 · 可手动生成"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* V1.5 P0-3：自主等级选择器（Level 0-4 循环切换） */}
              <button
                onClick={cycleAutonomy}
                title={`自主等级 ${AUTONOMY_LEVELS.find((l) => l.id === autonomyLevel)?.label}：${AUTONOMY_LEVELS.find((l) => l.id === autonomyLevel)?.desc}（点击循环切换，发布/付款/战略修改始终需人工确认）`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06] transition-colors"
              >
                <Activity size={11} className="text-cyan-400" />
                <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
                  {AUTONOMY_LEVELS.find((l) => l.id === autonomyLevel)?.label || "L1 AI辅助"}
                </span>
              </button>
              {/* Token 优化：手动生成晨报按钮 */}
              <button
                onClick={loadBriefing}
                disabled={briefingLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-500/15 border border-purple-500/40 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
                title="手动生成今日晨报（消耗 Token）"
              >
                {briefingLoading ? (
                  <Loader2 size={12} className="text-purple-400 animate-spin" />
                ) : (
                  <Sparkles size={12} className="text-purple-400" />
                )}
                <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider">
                  {briefingLoading ? "生成中..." : "生成今日晨报"}
                </span>
              </button>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-purple-500/10 border border-purple-500/30">
                {briefing.source === "enterprise_brain" ? (
                  <Brain size={12} className="text-purple-400" />
                ) : (
                  <Sparkles size={12} className="text-purple-400" />
                )}
                <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider">
                  {briefing.source === "enterprise_brain"
                    ? "Enterprise Brain"
                    : "Hermes 晨报"}
                </span>
              </div>
            </div>
            {/* 晨报生成结果提示（可见反馈） */}
            {briefingMessage && (
              <div
                className={`mt-2 px-3 py-1.5 rounded-md border text-[10px] font-mono ${
                  briefingMessage.type === "ok"
                    ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                    : "border-amber-500/30 bg-amber-500/[0.06] text-amber-300"
                }`}
              >
                <span className="whitespace-pre-line">{briefingMessage.text}</span>
              </div>
            )}

            {/* 查看 Daily 文件（闭环验证） */}
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => {
                  const next = !showDailyFiles;
                  setShowDailyFiles(next);
                  if (next && dailyFiles.length === 0) void loadDailyFiles();
                }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06] transition-colors"
                title="查看 10_Operations（运营层）/Daily/ 下已生成的文件（本地 Obsidian + 服务器同步）"
              >
                {dailyFilesLoading ? (
                  <Loader2 size={11} className="text-cyan-400 animate-spin" />
                ) : (
                  <FileText size={11} className="text-cyan-400" />
                )}
                <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
                  {showDailyFiles ? "收起 Daily 文件" : "查看 Daily 文件"}
                </span>
                {dailyFiles.length > 0 && (
                  <span className="text-[9px] font-mono px-1 rounded bg-cyan-500/15 text-cyan-300">
                    {dailyFiles.length}
                  </span>
                )}
              </button>
              <span className="text-[9px] font-mono text-gray-500">
                已自动同步至云端 · 落盘文件可在「查看 Daily 文件」中确认
              </span>
            </div>
            {showDailyFiles && (
              <div className="mt-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-2 max-h-44 overflow-y-auto">
                {dailyFilesLoading ? (
                  <p className="text-[10px] font-mono text-gray-500 py-2 text-center">加载中...</p>
                ) : dailyFiles.length === 0 ? (
                  <p className="text-[10px] font-mono text-gray-600 py-2 text-center">
                    暂无文件 — 点击「生成今日晨报」或到「战略计划」生成今日计划即可写入
                  </p>
                ) : (
                  dailyFiles.map((f) => (
                    <div
                      key={f.path}
                      className="flex items-center justify-between gap-2 py-1 px-1.5 rounded hover:bg-white/[0.03] border-b border-white/[0.03] last:border-b-0"
                    >
                      <span className="text-[10px] font-mono text-gray-300 truncate">
                        {f.name}
                      </span>
                      <span className="text-[8px] font-mono text-gray-600 flex-shrink-0">
                        {f.mtime ? f.mtime.slice(0, 16).replace("T", " ") : ""}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 今日优先级 3 件事 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={11}
                    className={
                      i < (briefing.overallPriority || 3)
                        ? "text-amber-400 fill-amber-400"
                        : "text-gray-700"
                    }
                  />
                ))}
              </div>
              <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                今日优先级
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              {briefing.priorities.length === 0 ? (
                <PriorityCardSkeleton />
              ) : (
                briefing.priorities.slice(0, 3).map((p, idx) => (
                  <PriorityCard
                    key={idx}
                    index={idx + 1}
                    priority={p}
                    onClick={() => setActivePriority(p)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {/* ============ 中部：4 个 KPI 卡片（P0-功能4 增强：sparkline+目标进度+降级标记） ============ */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
        className="grid grid-cols-4 gap-3 flex-shrink-0"
      >
        {kpiCards.map((card, idx) => (
          <KpiCard
            key={idx}
            label={card.label}
            subtitle={card.subtitle}
            value={card.value}
            icon={card.icon}
            color={card.color}
            deltaPercent={card.deltaPercent}
            deltaLabel={card.deltaLabel}
            target={card.target}
            actual={card.actual}
            progressUnit={card.progressUnit}
            trend={card.trend}
            degraded={card.degraded}
            dataSource={card.dataSource}
            onClick={card.onClick}
            animationDelay={0.1 + idx * 0.05}
          />
        ))}
      </motion.section>

      {/* ============ V1.5 P0-1：今日赚钱机会（Opportunity Radar） ============ */}
      <TodayOpportunityStrip />

      {/* ============ V1.5 P1-7：CEO Command Center 首屏指挥条 ============ */}
      <CommandCenterStrip
        revenue={ceoData.kpi.revenue}
        profit={ceoData.kpi.profit}
        cash={ceoData.kpi.cash}
        newCustomers={ceoData.kpi.newCustomers}
        contentCount={realKpi?.contentCount ?? 0}
        dailyRevenueTarget={goalTargets.dailyRevenue ?? undefined}
        dailyCustomerTarget={goalTargets.dailyCustomers ?? undefined}
        sourceStatus={ceoData.sourceStatus}
        degraded={ceoData.degraded}
        activities={activities}
      />

      {/* ============ V1.5 P1-7：企业部门面板折叠为「今日必看 / 更多」两级 ============ */}
      <div className="flex items-center gap-1 p-1 rounded-lg border border-white/[0.06] bg-white/[0.01] flex-shrink-0 overflow-x-auto">
        {/* 「今日必看」核心标签（老板每天最该看的结果页） */}
        <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider px-1 select-none">
          今日必看
        </span>
        {MUST_SEE_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeSection === tab.id;
          // as const 使 group 收窄为字面量，此处展开为宽联合避免 TS2367
          const group = tab.group as "P0" | "P1" | "P2" | "P3";
          const isP1 = group === "P1";
          const isP2 = group === "P2";
          const isP3 = group === "P3";
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-mono transition-all ${
                active
                  ? isP3
                    ? "text-white bg-amber-500/20 border border-amber-500/30"
                    : isP2
                    ? "text-white bg-emerald-500/20 border border-emerald-500/30"
                    : isP1
                    ? "text-white bg-purple-500/20 border border-purple-500/30"
                    : "text-white bg-cyan-500/20 border border-cyan-500/30"
                  : isP3
                  ? "text-amber-400/70 hover:text-amber-300 hover:bg-amber-500/[0.05] border border-transparent"
                  : isP2
                  ? "text-emerald-400/70 hover:text-emerald-300 hover:bg-emerald-500/[0.05] border border-transparent"
                  : isP1
                  ? "text-purple-400/70 hover:text-purple-300 hover:bg-purple-500/[0.05] border border-transparent"
                  : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] border border-transparent"
              }`}
              title={tab.label}
            >
              <Icon size={12} className={active ? (isP3 ? "text-amber-400" : isP2 ? "text-emerald-400" : isP1 ? "text-purple-400" : "text-cyan-400") : ""} />
              {isP1 && <span className="text-[8px] font-mono opacity-60">P1</span>}
              {isP2 && <span className="text-[8px] font-mono opacity-60">P2</span>}
              {isP3 && <span className="text-[8px] font-mono opacity-60">P3</span>}
              {tab.label}
            </button>
          );
        })}

        {/* 「更多」下拉（低频页签收入二级菜单） */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowMoreTabs(!showMoreTabs)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider transition-all ${
              showMoreTabs
                ? "bg-white/[0.06] text-white border border-white/[0.12]"
                : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
            }`}
          >
            <span>更多</span>
            <ChevronDown size={11} className={showMoreTabs ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
          {showMoreTabs && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowMoreTabs(false)} />
              <div className="absolute top-full left-0 mt-1 z-40 min-w-[180px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl">
                {MORE_TABS.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeSection === tab.id;
                  const group = tab.group as "P0" | "P1" | "P2" | "P3";
                  const isP1 = group === "P1";
                  const isP2 = group === "P2";
                  const isP3 = group === "P3";
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveSection(tab.id);
                        setShowMoreTabs(false);
                      }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                        active
                          ? isP3
                            ? "text-amber-300 bg-amber-500/10"
                            : isP2
                            ? "text-emerald-300 bg-emerald-500/10"
                            : isP1
                            ? "text-purple-300 bg-purple-500/10"
                            : "text-cyan-300 bg-cyan-500/10"
                          : "text-gray-300 hover:bg-white/[0.05]"
                      }`}
                    >
                      <Icon size={11} className={isP3 ? "text-amber-400" : isP2 ? "text-emerald-400" : isP1 ? "text-purple-400" : "text-cyan-400"} />
                      <span className="flex-1 text-left">
                        {isP1 && <span className="text-[8px] font-mono opacity-60">P1 </span>}
                        {isP2 && <span className="text-[8px] font-mono opacity-60">P2 </span>}
                        {isP3 && <span className="text-[8px] font-mono opacity-60">P3 </span>}
                        {tab.label}
                      </span>
                      {active && <span className="w-1.5 h-1.5 rounded-full bg-white/60" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab 内容区（页签级错误隔离：某个子组件渲染异常只影响当前区块，可切换页签恢复）
          v6.3.25 flex-shrink-0 修复布局挤压：外层为固定高 flex 布局，固定区块（晨报+KPI+Tab栏+实时流+计划）合计约 990px，
          内容区是唯一可压缩项且根节点 overflow-hidden → min-height 被压到 0 → OKR/经营会议/健康度/决策建议"下面没显示"。
          加 shrink-0 后内容区按内容自然高，由外层 overflow-y-auto 滚动。 */}
      <div className="flex-shrink-0">
      <ErrorBoundary key={activeSection} resetKey={activeSection} title="当前页签渲染异常">

      {/* Tab 0：智能晨报（CEO 智能晨报 P0-功能1） */}
      {activeSection === "morning" && (
        <>
          {/* ============ CEO 智能晨报（数据摘要+异常预警+AI建议+对标分析+写入Vault） ============ */}
          <CEOMorningReport />
          {/* ============ 昨天公司学到了什么（知识演化 Runtime 日报摘要） ============ */}
          <YesterdayLearningsCard />
        </>
      )}

      {/* Tab 0.5：决策建议（CEO 决策面板 P0-功能2） */}
      {activeSection === "decision" && (
        <>
          {/* ============ CEO 决策面板（AI建议卡片+规则引擎+一键执行+历史记录） ============ */}
          <CEODecisionPanel />
        </>
      )}

      {/* Tab 1：战略计划（年度目标 + 五层计划联动下钻） */}
      {activeSection === "strategy" && (
        <>
          {/* ============ 年度目标分解入口 ============ */}
          <AnnualGoalsEntry />
          {/* ============ 五层计划联动下钻视图（年→季→月→周→日） ============ */}
          <FiveLayerPlanDrilldown />
        </>
      )}

      {/* Tab 2：资产增长（5 大方向资产建设仪表板） */}
      {activeSection === "assets" && (
        <>
          {/* ============ 资产建设仪表板 ============ */}
          <AssetBuildingDashboard />
        </>
      )}

      {/* Tab 3：风险监控（五维风险预警雷达 + CRM 联动风险客户） */}
      {activeSection === "risk" && (
        <>
          {/* ============ P0-功能3：五维风险预警雷达 ============ */}
          <RiskRadarChart />
          {/* ============ CRM 联动风险监控面板 ============ */}
          <CrmRiskMonitorPanel risks={crmRisks} onJumpToCrm={() => setWorkspace("crm")} />
        </>
      )}

      {/* Tab 4：知识飞轮（软件操作→知识自动沉淀） */}
      {activeSection === "flywheel" && (
        <>
          {/* ============ 知识飞轮 ============ */}
          <KnowledgeFlywheelEntry />
        </>
      )}

      {/* ============ P1 总控台标签页 ============ */}

      {/* P1-Tab 1：OKR 管理（OKR 目标管理体系，五层下钻） */}
      {activeSection === "p1_okr" && (
        <>
          {/* ============ P1-功能5：OKR 目标管理体系 ============ */}
          <OKRManager />
        </>
      )}

      {/* P1-Tab 2：战略地图（平衡计分卡四维度） */}
      {activeSection === "p1_strategy_map" && (
        <>
          {/* ============ P1-功能6：战略地图 ============ */}
          <StrategyMap />
        </>
      )}

      {/* P1-Tab 3：经营会议模拟（CMO/CSO/CFO/COO 四部门汇报） */}
      {activeSection === "p1_meeting" && (
        <>
          {/* ============ P1-功能7：经营会议模拟 ============ */}
          <BusinessMeetingSimulator />
        </>
      )}

      {/* P1-Tab 4：企业健康度评分（五维度+趋势图+改进建议） */}
      {activeSection === "p1_health" && (
        <>
          {/* ============ P1-功能8：企业健康度评分 ============ */}
          <HealthScoreDashboard />
        </>
      )}

      {/* ============ P2 总控台标签页 ============ */}

      {/* P2-Tab 1：AI 团队看板（9 个 AI 岗位状态） */}
      {activeSection === "p2_ai_team" && (
        <>
          {/* ============ P2-功能9：AI 团队看板 ============ */}
          <AITeamDashboard />
        </>
      )}

      {/* P2-Tab 2：预测性经营模拟（30/90 天 + 多情景） */}
      {activeSection === "p2_predictive" && (
        <>
          {/* ============ P2-功能11：预测性经营模拟 ============ */}
          <PredictiveSimulator />
        </>
      )}

      {/* P2-Tab 3：人机协作管理（AI 做/人做的事 + 协作效率） */}
      {activeSection === "p2_human_ai" && (
        <>
          {/* ============ P2-功能10：人机协作管理 ============ */}
          <HumanMachineManagement />
        </>
      )}

      {/* P3-Tab 1：经营驾驶舱（获客→成交结果漏斗 + 运营状态 + 风险卡片 + 承诺账本，v6.45 盈利闭环） */}
      {activeSection === "p3_result" && (
        <>
          {/* ============ 经营驾驶舱（让系统做了什么变成系统带来了什么） ============ */}
          <BusinessResultDashboard />
        </>
      )}

      {/* P3-Tab 2：多角色议题辩论室（战略/风控/执行 + 主席裁决，v6.45 决策升级） */}
      {activeSection === "p3_debate" && (
        <>
          {/* ============ 多角色议题辩论室（对标李开复多智能体决策） ============ */}
          <DebateRoom />
        </>
      )}
      </ErrorBoundary>
      </div>

      {/* ============ 底部：Live Company 实时流 ============ */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
        className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md overflow-hidden flex-shrink-0 h-[280px] flex flex-col"
      >
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-full" />
            <h3 className="text-[13px] font-medium text-white tracking-tight">
              Live Company · 实时流
            </h3>
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
              {activities.length} events
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              animate={{ opacity: [1, 0.3, 1], scale: [1, 1.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-wider">
              LIVE
            </span>
          </div>
        </div>

        {/* 活动列表（带入场动画，新条目从顶部滑入） */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          <AnimatePresence initial={false}>
            {activities.length === 0 ? (
              <div className="text-center py-8 text-[11px] font-mono text-gray-500">
                <div className="mb-1 text-gray-400">暂无实时活动</div>
                <div className="text-[9px] text-gray-600 leading-relaxed">
                  运行工作流（如 GrowthFactory 编排）或生成内容后实时显示
                  <br />
                  系统启动 / 工作流执行等真实事件将自动记录在此
                </div>
              </div>
            ) : (
              activities.slice(0, 20).map((act, idx) => (
                <motion.div
                  key={act.id}
                  layout
                  initial={{ opacity: 0, x: -20, height: 0 }}
                  animate={{ opacity: 1, x: 0, height: "auto" }}
                  exit={{ opacity: 0, x: 20, height: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="flex items-center gap-3 py-1.5 px-2 border-b border-white/[0.03] last:border-b-0 hover:bg-white/[0.02]"
                >
                  {/* 时间戳 */}
                  <span className="text-[10px] font-mono text-gray-500 flex-shrink-0 w-12">
                    {formatTimeShort(act.timestamp)}
                  </span>

                  {/* 状态点 */}
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{
                      background: getStatusColor(act.status),
                      boxShadow: `0 0 6px ${getStatusColor(act.status)}`,
                    }}
                  />

                  {/* 摘要 */}
                  <span className="text-[12px] text-gray-300 truncate flex-1">
                    {act.summary}
                  </span>

                  {/* 来源 */}
                  {act.source && (
                    <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider flex-shrink-0">
                      {act.source}
                    </span>
                  )}

                  {/* 序号 */}
                  {idx === 0 && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-0.5 text-[9px] font-mono text-emerald-400 uppercase tracking-wider flex-shrink-0"
                    >
                      <Activity size={9} />
                      NEW
                    </motion.span>
                  )}
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </motion.section>

      {/* ============ 底部：战略计划与闭环审核 ============ */}
      <PlansPanel />

      {/* ============ 优先级详情 Modal（点击顶栏 01/02/03 触发） ============ */}
      <AnimatePresence>
        {activePriority && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActivePriority(null)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden"
            >
              {/* 头部色带 */}
              <div
                className="h-1"
                style={{
                  background:
                    activePriority.level === "high"
                      ? "linear-gradient(90deg, #F87171, #EF4444)"
                      : activePriority.level === "medium"
                      ? "linear-gradient(90deg, #FBBF24, #F59E0B)"
                      : "linear-gradient(90deg, #60A5FA, #3B82F6)",
                }}
              />
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[24px] font-mono font-bold"
                      style={{
                        color:
                          activePriority.level === "high"
                            ? "#F87171"
                            : activePriority.level === "medium"
                            ? "#FBBF24"
                            : "#60A5FA",
                      }}
                    >
                      {String(briefing.priorities.indexOf(activePriority) + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="text-[14px] font-semibold text-white">
                        {activePriority.title}
                      </h3>
                      <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                        {activePriority.source || "Priority"} ·{" "}
                        {activePriority.level === "high"
                          ? "高优先级"
                          : activePriority.level === "medium"
                          ? "中优先级"
                          : "低优先级"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActivePriority(null)}
                    className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-3">
                  {activePriority.rationale && (
                    <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-3">
                      <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                        为什么重要
                      </div>
                      <p className="text-[12px] text-gray-200 leading-relaxed">
                        {activePriority.rationale}
                      </p>
                    </div>
                  )}
                  {activePriority.expectedOutcome && (
                    <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
                      <div className="text-[10px] font-mono text-emerald-400/80 uppercase tracking-wider mb-1">
                        预期成果
                      </div>
                      <p className="text-[12px] text-gray-200 leading-relaxed">
                        {activePriority.expectedOutcome}
                      </p>
                    </div>
                  )}
                  {activePriority.product && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-gray-500 font-mono">涉及产品：</span>
                      <span className="px-2 py-0.5 rounded bg-purple-500/15 border border-purple-500/30 text-purple-300 font-mono">
                        {activePriority.product}
                      </span>
                    </div>
                  )}
                  {!activePriority.rationale &&
                    !activePriority.expectedOutcome &&
                    !activePriority.product && (
                      <p className="text-[12px] text-gray-500 text-center py-4">
                        该优先级条目由 {activePriority.source || "活动流"} 派生，暂无详细说明。
                        <br />
                        可点击右上角"生成今日晨报"调用 EnterpriseBrain 获取详细战略分析。
                      </p>
                    )}
                </div>

                <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-end gap-2">
                  <button
                    onClick={() => setActivePriority(null)}
                    className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] rounded transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：优先级卡片
// ============================================

interface PriorityCardProps {
  index: number;
  priority: MorningPriority;
  onClick?: () => void;
}

function PriorityCard({ index, priority, onClick }: PriorityCardProps) {
  const color =
    priority.level === "high"
      ? "#F87171"
      : priority.level === "medium"
      ? "#FBBF24"
      : "#60A5FA";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 * index }}
      onClick={onClick}
      className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:bg-white/[0.04] hover:border-white/[0.12] transition-colors group cursor-pointer"
    >
      {/* 左侧色条 */}
      <div
        className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full"
        style={{ background: color }}
      />
      <div className="pl-2">
        <div className="flex items-center justify-between mb-1">
          <span
            className="text-[14px] font-mono font-bold"
            style={{ color }}
          >
            {index.toString().padStart(2, "0")}
          </span>
          {priority.source && (
            <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">
              {priority.source}
            </span>
          )}
        </div>
        <p className="text-[12px] text-gray-200 leading-snug">
          {priority.title}
        </p>
        {/* 查看详情提示 */}
        <div className="mt-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight size={9} className="text-gray-500" />
          <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
            点击查看详情
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function PriorityCardSkeleton() {
  return (
    <div className="col-span-3 py-4 text-center text-[11px] font-mono text-gray-500">
      正在生成今日优先级...
    </div>
  );
}

// ============================================
// 子组件：CRM 联动风险监控面板
// ============================================

interface CrmRiskMonitorPanelProps {
  risks: CrmRiskItem[];
  onJumpToCrm: () => void;
}

function CrmRiskMonitorPanel({ risks, onJumpToCrm }: CrmRiskMonitorPanelProps) {
  const staleRisks = risks.filter((r) => r.type === "stale");
  const closingRisks = risks.filter((r) => r.type === "closing");
  const isEmpty = risks.length === 0;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.05, ease: "easeOut" }}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md overflow-hidden flex-shrink-0"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-red-400 to-red-600 rounded-full" />
          <h3 className="text-[13px] font-medium text-white tracking-tight">
            风险监控 · CRM 联动
          </h3>
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            {risks.length} alerts
          </span>
        </div>
        <button
          onClick={onJumpToCrm}
          className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-[10px] font-mono text-gray-400 hover:text-white"
          title="跳转到 CRM 客户中心处理"
        >
          <ArrowUpRight size={11} />
          前往 CRM
        </button>
      </div>

      {/* 风险列表 */}
      <div className="p-3">
        {isEmpty ? (
          <div className="text-center py-4 text-[11px] font-mono text-gray-500">
            暂无风险客户 · CRM 数据加载中或无风险项
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {/* 30天未联系 */}
            <div className="rounded-lg border border-red-500/15 bg-red-500/[0.03] p-2.5">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle size={12} className="text-red-400" />
                <span className="text-[11px] font-mono font-semibold text-red-300 uppercase tracking-wider">
                  30天未联系
                </span>
                <span className="text-[10px] font-mono text-red-400/70">
                  {staleRisks.length}
                </span>
              </div>
              <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1">
                {staleRisks.length === 0 ? (
                  <div className="text-[10px] font-mono text-gray-600 italic py-1">
                    无
                  </div>
                ) : (
                  staleRisks.map((r, i) => (
                    <div
                      key={`stale-${i}`}
                      className="flex items-center gap-2 px-2 py-1 rounded border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                    >
                      <span className="text-[11px] text-gray-200 truncate flex-1">
                        {r.customerName}
                      </span>
                      <span className="text-[9px] font-mono text-red-400 flex-shrink-0">
                        {r.daysSinceContact}天
                      </span>
                      {r.industry && (
                        <span className="text-[9px] font-mono text-gray-600 flex-shrink-0 truncate max-w-[60px]">
                          {r.industry}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 快成交未跟进 */}
            <div className="rounded-lg border border-amber-500/15 bg-amber-500/[0.03] p-2.5">
              <div className="flex items-center gap-1.5 mb-2">
                <Flame size={12} className="text-amber-400" />
                <span className="text-[11px] font-mono font-semibold text-amber-300 uppercase tracking-wider">
                  快成交未跟进
                </span>
                <span className="text-[10px] font-mono text-amber-400/70">
                  {closingRisks.length}
                </span>
              </div>
              <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1">
                {closingRisks.length === 0 ? (
                  <div className="text-[10px] font-mono text-gray-600 italic py-1">
                    无
                  </div>
                ) : (
                  closingRisks.map((r, i) => (
                    <div
                      key={`closing-${i}`}
                      className="flex items-center gap-2 px-2 py-1 rounded border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                    >
                      <span className="text-[11px] text-gray-200 truncate flex-1">
                        {r.customerName}
                      </span>
                      <span className="text-[9px] font-mono text-amber-400 flex-shrink-0">
                        {r.stage}
                      </span>
                      {r.industry && (
                        <span className="text-[9px] font-mono text-gray-600 flex-shrink-0 truncate max-w-[60px]">
                          {r.industry}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
}

// ============================================
// 工具函数
// ============================================

/**
 * 从 EnterpriseBrain 返回数据中提取优先级列表
 *
 * EnterpriseBrain 的 TodayFocus 结构：
 *   { date, priorities: [{ rank, title, rationale, expectedOutcome, product }], overallPriority, risks, opportunities }
 */
function extractBrainPriorities(data: any): MorningPriority[] {
  if (!data) return [];

  // EnterpriseBrain.get_today_focus 返回的 TodayFocus
  if (Array.isArray(data.priorities)) {
    return data.priorities.map((p: any) => ({
      title: String(p?.title || ""),
      level: p?.rank === 1 ? "high" : p?.rank === 2 ? "medium" : "low",
      source: "EnterpriseBrain",
      rationale: p?.rationale || "",
      expectedOutcome: p?.expectedOutcome || "",
      product: p?.product || "",
    }));
  }

  // 兼容 BrainOutput 结构
  if (data.todayFocus && Array.isArray(data.todayFocus.priorities)) {
    return data.todayFocus.priorities.map((p: any) => ({
      title: String(p?.title || ""),
      level: p?.rank === 1 ? "high" : p?.rank === 2 ? "medium" : "low",
      source: "EnterpriseBrain",
      rationale: p?.rationale || "",
      expectedOutcome: p?.expectedOutcome || "",
      product: p?.product || "",
    }));
  }

  return [];
}

/** 从 BusinessOperator 返回数据中提取优先级列表 */
function extractPriorities(data: any): MorningPriority[] {
  // 兼容多种返回结构
  if (!data) return [];
  // 1) 直接是 OperatorReport
  if (Array.isArray(data.tomorrowPriorities)) {
    return data.tomorrowPriorities.map((p: any) => ({
      title: String(p?.title || p?.text || p?.summary || ""),
      level: mapLevel(p?.priority || p?.level),
      source: p?.source || p?.runtime || "Operator",
    }));
  }
  // 2) 嵌套在 ceoBriefing 中
  if (data.ceoBriefing && Array.isArray(data.ceoBriefing.priorities)) {
    return data.ceoBriefing.priorities.map((p: any) => ({
      title: String(p?.title || p?.text || ""),
      level: mapLevel(p?.priority || p?.level),
      source: p?.source || "CEO Briefing",
    }));
  }
  // 3) checklist 中的明日优先级
  if (Array.isArray(data.checklist)) {
    const item = data.checklist.find(
      (c: any) => c?.id === "tomorrow_priorities"
    );
    if (item?.details?.priorities) {
      return item.details.priorities.map((p: any) => ({
        title: String(p?.title || p?.text || ""),
        level: mapLevel(p?.priority || p?.level),
        source: p?.source || "Operator",
      }));
    }
  }
  return [];
}

function mapLevel(level: any): MorningPriority["level"] {
  if (level === "high" || level === "P0" || level === "critical") return "high";
  if (level === "medium" || level === "P1") return "medium";
  return "low";
}

/** 降级：从 activities 派生 3 件事 */
function derivePrioritiesFromActivities(
  activities: { summary: string; source?: string; status: string }[]
): MorningPriority[] {
  if (activities.length === 0) {
    return [
      { title: "今日暂无活动记录（真实状态）— 到战略计划生成今日计划，或点击上方「生成今日晨报」", level: "medium", source: "System" },
      { title: "今日任务将由 OKR 日目标自动拆解，配置年度目标后生效", level: "low", source: "System" },
      { title: "数据源：财务中心录入 / CRM 建档 / 内容工厂生产后自动汇总", level: "low", source: "System" },
    ];
  }
  // 取最新 3 条活动作为优先级
  return activities.slice(0, 3).map((a) => ({
    title: a.summary,
    level: a.status === "failed" ? "high" : "medium",
    source: a.source || "Activity",
  }));
}

/** 从今日计划（Daily/*.md）解析真实任务作为晨报优先级 */
function derivePrioritiesFromDailyPlan(content: string): MorningPriority[] {
  const priorities: MorningPriority[] = [];
  const lines = content.split("\n");

  // 1. 提取任务清单：- [ ] 任务（含时间）
  for (const line of lines) {
    const m = line.match(/^\s*[-*]\s*\[( |x)\]\s*(.+)$/i);
    if (!m) continue;
    const raw = m[2].trim();
    if (!raw || raw.length < 2 || /^待确定/.test(raw)) continue;
    const done = m[1].toLowerCase() === "x";
    priorities.push({
      title: raw.length > 60 ? raw.slice(0, 60) + "…" : raw,
      level: done ? "low" : /^\d{1,2}:\d{2}/.test(raw) ? "high" : "medium",
      source: "今日计划",
      rationale: done ? "已完成" : undefined,
    });
    if (priorities.length >= 6) break;
  }

  // 2. 若任务不足 3 条，提取「今日核心目标」小节
  if (priorities.length < 3) {
    let inGoals = false;
    for (const line of lines) {
      if (/今日核心目标/.test(line)) inGoals = true;
      else if (inGoals && /^#{1,4}\s/.test(line)) inGoals = false;
      if (!inGoals) continue;
      const m = line.match(/^\s*[-*]\s*(.+)$/);
      if (!m) continue;
      const raw = m[1].trim();
      if (!raw || raw.length < 4) continue;
      priorities.push({
        title: raw.length > 60 ? raw.slice(0, 60) + "…" : raw,
        level: priorities.length < 3 ? "high" : "medium",
        source: "今日计划",
      });
      if (priorities.length >= 3) break;
    }
  }

  return priorities.slice(0, 3);
}

function getStatusColor(status: string): string {
  switch (status) {
    case "running":
      return "#38BDF8";
    case "success":
      return "#34D399";
    case "failed":
      return "#F87171";
    case "warning":
      return "#FBBF24";
    default:
      return "#9CA3AF";
  }
}

function formatTimeShort(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}
